import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { reportsAPI, milestonesAPI, ideasAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  LayoutGrid, 
  Lightbulb, 
  Calendar,
  ArrowRight,
  TrendingUp,
  CheckCircle
} from 'lucide-react';

const MILESTONE_STATUS_BADGE = {
  completed: 'bg-green-100 text-green-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
};

const IDEA_STATUS_BADGE = {
  accepted: 'bg-green-100 text-green-700',
  analyzed: 'bg-blue-100 text-blue-700',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentIdeas, setRecentIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, tasksRes, ideasRes] = await Promise.all([
        reportsAPI.getDashboard(),
        milestonesAPI.getAll(),
        ideasAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setRecentTasks(tasksRes.data.slice(0, 5));
      setRecentIdeas(ideasRes.data.slice(0, 5));
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Dashboard">
      <div className="space-y-6" data-testid="dashboard-content">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-[#101010] to-[#2d2d2d] rounded-lg p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">
            Welcome back, {user?.name}!
          </h2>
          <p className="text-gray-300">
            You are logged in as <span className="font-medium text-[#CFFF40] capitalize">{user?.role}</span>
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Today is {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* KPI Cards - Admin */}
        {user?.role === 'admin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Active Restaurants"
              value={stats?.restaurants?.active || 0}
              subtitle={`${stats?.restaurants?.trial || 0} in trial`}
              icon={Users}
              color="#CFFF40"
              onClick={() => navigate('/restaurants')}
            />
            <StatCard
              title="Active Modules"
              value={stats?.modules?.active || 0}
              subtitle={`${stats?.modules?.in_development || 0} in development`}
              icon={LayoutGrid}
              color="#FFFF66"
              onClick={() => navigate('/modules')}
            />
            <StatCard
              title="Ideas Submitted"
              value={stats?.ideas?.this_month || 0}
              subtitle={`${stats?.ideas?.accepted || 0} accepted`}
              icon={Lightbulb}
              color="#FF6B35"
              onClick={() => navigate('/ideas')}
            />
            <StatCard
              title="Milestones Completed"
              value={stats?.dev_velocity?.completed_tasks || 0}
              subtitle={`${stats?.dev_velocity?.in_progress_tasks || 0} in progress`}
              icon={CheckCircle}
              color="#3B82F6"
              onClick={() => navigate('/kanban')}
            />
          </div>
        )}

        {/* Developer cards */}
        {user?.role === 'developer' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="My Milestones"
              value={recentTasks.length}
              subtitle="assigned to you"
              icon={Calendar}
              color="#CFFF40"
              onClick={() => navigate('/kanban')}
            />
            <StatCard
              title="In Progress"
              value={recentTasks.filter(t => t.status === 'in_progress').length}
              subtitle="milestones"
              icon={TrendingUp}
              color="#FFFF66"
              onClick={() => navigate('/kanban')}
            />
            <StatCard
              title="Completed"
              value={recentTasks.filter(t => t.status === 'completed').length}
              subtitle="milestones"
              icon={CheckCircle}
              color="#22C55E"
              onClick={() => navigate('/kanban')}
            />
          </div>
        )}

        {/* Partner cards */}
        {user?.role === 'partner' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="Ideas from Restaurants"
              value={recentIdeas.length}
              subtitle="total ideas"
              icon={Lightbulb}
              color="#CFFF40"
              onClick={() => navigate('/ideas')}
            />
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
              <h3 className="font-semibold text-[#111111] mb-4">Quick Actions</h3>
              <Button
                onClick={() => navigate('/submit-idea')}
                className="w-full bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
              >
                Submit New Idea
              </Button>
            </div>
          </div>
        )}

        {user?.role === 'restaurant' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="My Ideas"
              value={recentIdeas.length}
              subtitle="submitted"
              icon={Lightbulb}
              color="#CFFF40"
              onClick={() => navigate('/ideas')}
            />
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
              <h3 className="font-semibold text-[#111111] mb-4">Quick Actions</h3>
              <Button
                onClick={() => navigate('/submit-idea')}
                className="w-full bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
              >
                Submit New Idea
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Milestones */}
          {(user?.role === 'admin' || user?.role === 'developer') && (
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#111111]">Recent Milestones</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/roadmap')}>
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {recentTasks.slice(0, 5).map(task => (
                  <div key={task.id} className="flex items-center justify-between py-2 border-b border-[#E0E0E0] last:border-0">
                    <div>
                      <p className="text-sm font-medium text-[#111111]">{task.title}</p>
                      <p className="text-xs text-[#666666]">{task.module_name}</p>
                    </div>
                    <Badge className={MILESTONE_STATUS_BADGE[task.status] || 'bg-gray-100 text-gray-700'}>
                      {task.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
                {recentTasks.length === 0 && (
                  <p className="text-sm text-[#666666] text-center py-4">No milestones yet</p>
                )}
              </div>
            </div>
          )}

          {/* Recent Ideas */}
          {(user?.role === 'admin' || user?.role === 'partner' || user?.role === 'restaurant') && (
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#111111]">Recent Ideas</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/ideas')}>
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {recentIdeas.slice(0, 5).map(idea => (
                  <div key={idea.id} className="flex items-center justify-between py-2 border-b border-[#E0E0E0] last:border-0">
                    <div>
                      <p className="text-sm font-medium text-[#111111]">{idea.title}</p>
                      <p className="text-xs text-[#666666]">{idea.restaurant_name || 'Unknown'}</p>
                    </div>
                    <Badge className={IDEA_STATUS_BADGE[idea.status] || 'bg-gray-100 text-gray-700'}>
                      {idea.status}
                    </Badge>
                  </div>
                ))}
                {recentIdeas.length === 0 && (
                  <p className="text-sm text-[#666666] text-center py-4">No ideas yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ title, value, change, subtitle, icon: Icon, color, onClick }) {
  return (
    <div 
      className="bg-white border border-[#E0E0E0] rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[#666666]">{title}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color }}>
          <Icon size={16} className="text-[#101010]" />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#111111]">{value}</p>
      {subtitle && (
        <p className="text-sm text-[#666666]">{subtitle}</p>
      )}
    </div>
  );
}
